import { Helmet } from "react-helmet-async";
import { useTranslation, LanguageContext } from "../locales/LanguageContext";
import { useContext } from "react";

const About = () => {
  const t = useTranslation();
  const { lang } = useContext(LanguageContext);

  // Philosophy points for both languages
  const philosophyPoints =
    lang === "pa"
      ? [
          { icon: "📖", text: "ਗੁਰਬਾਣੀ ਸਿਰਫ਼ ਪਾਠ ਨਹੀਂ, ਜੀਵਨ ਦਾ ਮਾਰਗ ਹੈ।" },
          { icon: "🧠", text: "ਗੁਰਬਾਣੀ ਕੰਠ ਕਰਨੀ ਮਨ ਨੂੰ ਮਜ਼ਬੂਤ ਬਣਾਉਂਦੀ ਹੈ।" },
          { icon: "✍️", text: "ਗੁਰਬਾਣੀ ਲਿਖਣਾ ਧਿਆਨ ਅਤੇ ਅਨੁਸ਼ਾਸਨ ਪੈਦਾ ਕਰਦਾ ਹੈ" },
          {
            icon: "🌱",
            text: "ਸਿੱਖਿਆ ਪਿਆਰ, ਸਤਿਕਾਰ ਅਤੇ ਸਮਝ ਨਾਲ ਹੋਣੀ ਚਾਹੀਦੀ ਹੈ।",
          },
        ]
      : [
          { icon: "📖", text: "Gurbani is not just to be read, but lived" },
          {
            icon: "🧠",
            text: "Memorization builds inner strength and clarity",
          },
          { icon: "✍️", text: "Writing Gurbani deepens focus and discipline" },
          {
            icon: "🌱",
            text: "Learning must be gentle, respectful, and value-based",
          },
        ];

  return (
    <>
      <Helmet>
        <title>AKAAL KIDS | {t("aboutTitle")}</title>
      </Helmet>
      {/* Hero Banner */}
      <div
        style={{
          width: "100%",
          background: "linear-gradient(90deg, #f6815e 0%, #f6b93b 100%)",
          color: "#fff",
          padding: "38px 0 24px 0",
          textAlign: "center",
          marginBottom: 32,
          boxShadow: "0 8px 32px #f6815e33",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <h1
          style={{
            fontWeight: 900,
            fontSize: "2.6rem",
            letterSpacing: 1,
            marginBottom: 8,
            textShadow: "0 2px 16px #f6815e77",
            animation: "fadeInDown 1s",
            transition: "transform 0.3s",
          }}
        >
          {t("aboutTitle")}{" "}
          <span
            style={{
              color: "#fff",
              background: "rgba(0,0,0,0.13)",
              borderRadius: 12,
              padding: "0 14px",
              fontSize: "1.1em",
              boxShadow: "0 2px 8px #f6b93b33",
            }}
          >
            AKAAL KIDS
          </span>
        </h1>
        <div style={{ fontSize: 20, fontWeight: 500, opacity: 0.96 }}>
          <span role="img" aria-label="star">
            🌟
          </span>{" "}
          {t("subtitle")}
        </div>
      </div>

      <div className="container pb-5">
        {/* Introduction */}
        <div className="row justify-content-center mb-5">
          <div className="col-lg-10 text-center">
            <p
              style={{ fontSize: "1.25rem", lineHeight: "1.8", color: "#444" }}
            >
              {t("aboutText")
                .split("\n")
                .map((line, i) => (
                  <span key={i}>
                    {line}
                    <br />
                  </span>
                ))}
            </p>
          </div>
        </div>

        {/* Foundation Section */}
        <div
          className="card border-0 shadow-sm mb-5"
          style={{ borderRadius: 20, overflow: "hidden" }}
        >
          <div className="card-body p-5" style={{ background: "#fffbe6" }}>
            <h2 style={{ color: "#b26a00", fontWeight: 800, marginBottom: 24 }}>
              {t("foundationHeading")}
            </h2>
            <p style={{ fontSize: "1.1rem" }}>
              {t("foundationText")
                .split("\n")
                .map((line, i) => (
                  <span key={i}>
                    {line}
                    <br />
                  </span>
                ))}
            </p>
          </div>
        </div>

        {/* Why Akaal Kids & Inspiration Grid */}
        <div className="row g-4 mb-5">
          {/* Why Created */}
          <div className="col-lg-6">
            <div
              className="card h-100 border-0 shadow-sm p-4"
              style={{ borderRadius: 20 }}
            >
              <h3
                style={{ color: "#f6815e", fontWeight: 800, marginBottom: 20 }}
              >
                {t("whyHeading")}
              </h3>
              <p>
                {t("whyText")
                  .split("\n")
                  .map((line, i) => (
                    <span key={i}>
                      {line}
                      <br />
                    </span>
                  ))}
              </p>
            </div>
          </div>

          {/* Inspiration */}
          <div className="col-lg-6">
            <div
              className="card h-100 border-0 shadow-sm p-4"
              style={{
                borderRadius: 20,
                background: "linear-gradient(135deg, #fff 0%, #fff8e1 100%)",
              }}
            >
              <h3
                style={{ color: "#b26a00", fontWeight: 800, marginBottom: 20 }}
              >
                {t("inspirationHeading")}
              </h3>
              <p>
                {t("inspirationText")
                  .split("\n")
                  .map((line, i) => (
                    <span key={i}>
                      {line}
                      <br />
                    </span>
                  ))}
              </p>
            </div>
          </div>
        </div>

        {/* Philosophy Section */}
        <div className="mb-5">
          <h2
            className="text-center mb-4"
            style={{ color: "#333", fontWeight: 800 }}
          >
            {t("philosophyHeading")}
          </h2>
          <div className="row g-3">
            {philosophyPoints.map((item, idx) => (
              <div key={idx} className="col-md-6 col-lg-3">
                <div
                  className="card h-100 border-0 shadow-sm text-center p-3"
                  style={{ borderRadius: 16, background: "#f8f9fa" }}
                >
                  <div style={{ fontSize: "2rem", marginBottom: 10 }}>
                    {item.icon}
                  </div>
                  <p style={{ fontWeight: 600, color: "#555", margin: 0 }}>
                    {item.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p
            className="text-center mt-4"
            style={{ fontSize: "1.1rem", color: "#666" }}
          >
            {t("philosophyText")
              .split("\n")
              .map((line, i) => (
                <span key={i}>
                  {line}
                  <br />
                </span>
              ))}
          </p>
        </div>

        {/* Who Can Join & Vision */}
        <div className="row g-4">
          <div className="col-md-6">
            <div
              className="card h-100 border-0 shadow-lg text-white"
              style={{
                borderRadius: 20,
                background: "linear-gradient(135deg, #f6815e 0%, #f6b93b 100%)",
                padding: "2rem",
              }}
            >
              <h3 style={{ fontWeight: 800, marginBottom: 20 }}>
                {t("whoHeading")}
              </h3>
              <p style={{ fontSize: "1.2rem" }}>
                {t("whoText")
                  .split("\n")
                  .map((line, i) => (
                    <span key={i}>
                      {line}
                      <br />
                    </span>
                  ))}
              </p>
            </div>
          </div>
          <div className="col-md-6">
            <div
              className="card h-100 border-0 shadow-sm p-4 d-flex flex-column justify-content-center text-center"
              style={{ borderRadius: 20, background: "#fff" }}
            >
              <h3
                style={{ color: "#b26a00", fontWeight: 800, marginBottom: 20 }}
              >
                {t("visionHeading")}
              </h3>
              <div
                style={{
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  color: "#444",
                  marginBottom: 20,
                }}
              >
                {t("visionText")
                  .split("\n")
                  .map((line, i) => (
                    <span key={i}>
                      {line}
                      <br />
                    </span>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;
